// ABOUTME: Functional Groups page — the FIG stakeholder groups interviewed in the Listening Tour
// ABOUTME: Clean, evidence-honest presentation; metrics fill in as interviews are ingested

import { Link } from 'react-router-dom';
import { usePersonas } from '@/hooks/usePersonas';
import { PersonaDefinition, PersonaMetrics } from '@/api/personas';

export const PersonasPage = () => {
  const { data, isLoading, error } = usePersonas();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-alx-blue/20 border-t-alx-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Couldn't load functional groups. {error instanceof Error ? error.message : ''}
        </div>
      </div>
    );
  }

  if (!data || data.definitions.length === 0) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-8 text-gray-500">
        No functional groups defined yet.
      </div>
    );
  }

  const metricsMap = new Map<string, PersonaMetrics>();
  data.metrics.forEach((m) => metricsMap.set(m.persona_type, m));
  const totalInterviews = data.metrics.reduce((s, m) => s + (m.interview_count || 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Header */}
      <header className="mb-8 border-l-2 border-alx-green pl-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-alx-cyan">
          Listening Tour
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-alx-blue">Functional Groups</h1>
        <p className="mt-2 max-w-2xl leading-relaxed text-gray-600">
          The stakeholder groups interviewed across ALX, ALU, ALA and FIT. Every interview is
          evaluated against all hypotheses, so coverage builds up group by group.
        </p>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
          <span>
            <span className="font-semibold text-alx-blue">{data.definitions.length}</span> groups
          </span>
          <span>
            <span className="font-semibold tabular-nums text-alx-blue">{totalInterviews}</span>{' '}
            interviews ingested
          </span>
        </div>
      </header>

      {/* Groups */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data.definitions.map((p) => (
          <GroupCard key={p.persona_type} persona={p} metrics={metricsMap.get(p.persona_type)} />
        ))}
      </div>
    </div>
  );
};

const GroupCard = ({
  persona,
  metrics,
}: {
  persona: PersonaDefinition;
  metrics?: PersonaMetrics;
}) => {
  const count = metrics?.interview_count ?? 0;
  const hasDetail =
    persona.typical_goals.length > 0 ||
    persona.typical_constraints.length > 0 ||
    persona.signal_phrases.length > 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 transition hover:border-alx-cyan/40 hover:shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Click through to the interviews list pre-filtered to this group */}
          <h2 className="text-lg font-semibold text-alx-blue">
            <Link
              to={`/dashboard/interviews?group=${encodeURIComponent(persona.persona_type)}`}
              className="hover:underline"
            >
              {persona.persona_type}
            </Link>
          </h2>
          {persona.persona_code && (
            <span className="mt-1 inline-block rounded bg-alx-blue/5 px-1.5 py-0.5 font-mono text-[11px] text-alx-cyan">
              {persona.persona_code}
            </span>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold tabular-nums text-alx-blue">{count}</div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400">
            {count === 1 ? 'interview' : 'interviews'}
          </div>
        </div>
      </div>

      {persona.description && (
        <p className="mt-3 text-sm leading-relaxed text-gray-600">{persona.description}</p>
      )}

      {hasDetail && (
        <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
          {persona.typical_goals.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-gray-500">Focus</div>
              <div className="flex flex-wrap gap-1.5">
                {persona.typical_goals.map((t, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-alx-green/10 px-2.5 py-0.5 text-xs text-alx-blue"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {persona.typical_constraints.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-gray-500">Constraints</div>
              <div className="flex flex-wrap gap-1.5">
                {persona.typical_constraints.map((t, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs text-red-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {persona.signal_phrases.length > 0 && (
            <details>
              <summary className="cursor-pointer list-none text-xs font-medium text-alx-cyan">
                Signal phrases ({persona.signal_phrases.length})
              </summary>
              <div className="mt-2 space-y-1">
                {persona.signal_phrases.map((s, i) => (
                  <p key={i} className="text-sm italic text-gray-500">
                    "{s}"
                  </p>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
